import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Enum-like object: string keys with number|string values (same shape as numeric TS enums)
type EnumLike = Record<string, number | string>;

function extractSteps(e: EnumLike): number[] {
	// Take only numeric values and sort ascending
	const nums = Object.values(e).filter(
		(v): v is number => typeof v === 'number'
	);
	// Guard against duplicates from enum reverse-mapping
	const unique = Array.from(new Set(nums));
	unique.sort((a, b) => a - b);
	return unique;
}

export function useLinearFlowSteps<TEnum extends EnumLike>(
	stepsEnum: TEnum,
	initialStep?: TEnum[keyof TEnum]
) {
	// Memoize deriving the steps array from the enum
	const computedSteps = useMemo(() => extractSteps(stepsEnum), [stepsEnum]);

	// Keep current steps and activeStep in refs for stable callbacks
	const stepsRef = useRef<number[]>(computedSteps);
	stepsRef.current = computedSteps;

	const [activeStep, setActiveStep] = useState<number>(() => {
		const i = Number(initialStep as unknown as number | undefined);
		return Number.isFinite(i) && computedSteps.includes(i)
			? i
			: computedSteps[0];
	});

	const activeStepRef = useRef<number>(activeStep);
	useEffect(() => {
		activeStepRef.current = activeStep;
	}, [activeStep]);

	// If initialStep changes from outside, sync the active step
	useEffect(() => {
		const i = Number(initialStep as unknown as number | undefined);
		if (
			Number.isFinite(i) &&
			stepsRef.current.includes(i) &&
			i !== activeStepRef.current
		) {
			setActiveStep(i);
		}
	}, [initialStep]);

	// If the steps set changes so that the current step is gone, fall back to the first step
	useEffect(() => {
		if (!stepsRef.current.includes(activeStepRef.current)) {
			setActiveStep(stepsRef.current[0]);
		}
	}, [computedSteps]);

	const getIndex = useCallback((value: number) => {
		return stepsRef.current.indexOf(value);
	}, []);

	const isFirst = getIndex(activeStep) === 0;
	const isLast = getIndex(activeStep) === stepsRef.current.length - 1;

	const setNextStep = useCallback((): boolean => {
		const steps = stepsRef.current;
		const curr = activeStepRef.current;
		const idx = steps.indexOf(curr);
		if (idx < 0 || idx >= steps.length - 1) return false;
		setActiveStep(steps[idx + 1]);
		return true;
	}, []);

	const setPrevStep = useCallback((): boolean => {
		const steps = stepsRef.current;
		const curr = activeStepRef.current;
		const idx = steps.indexOf(curr);
		if (idx <= 0) return false;
		setActiveStep(steps[idx - 1]);
		return true;
	}, []);

	const goToStep = useCallback((step: TEnum[keyof TEnum]): boolean => {
		const value = step as unknown as number;
		const steps = stepsRef.current;
		if (!steps.includes(value)) return false;
		if (value === activeStepRef.current) return false;
		setActiveStep(value);
		return true;
	}, []);

	const reset = useCallback((toStep?: TEnum[keyof TEnum]) => {
		const value =
			(toStep as unknown as number | undefined) ?? stepsRef.current[0];
		if (stepsRef.current.includes(value)) {
			setActiveStep(value);
		}
	}, []);

	return {
		activeStep: activeStep as TEnum[keyof TEnum],
		isFirst,
		isLast,
		stepsCount: stepsRef.current.length,
		setNextStep,
		setPrevStep,
		goToStep,
		reset,
	};
}
